import { SearchRounded } from "@mui/icons-material";
import { Alert, Box, CircularProgress, Container, Grid, InputAdornment, Paper, Stack, TextField, Typography } from "@mui/material";
import { useLanguage } from "../../../../contexts/LanguageContext";
import { OfferCard } from "./OfferCard";
import { TagFilter } from "./TagFilter";
import translations from "./index.translations.json";
import { useEffect, useState } from "react";
import { getOfferListByTag } from "@services/api/offer/getListByTag/getListByTag";
import { useSearchParams } from "react-router-dom";
import PaginationComponent from "@components/common/Pagination";
import { useDebounce } from "@hooks/useDebounce";
import FilterCategory from "@components/ui/offer-category/FilterCategory";

type SupportedLanguage = keyof typeof translations;

export const PublicOfferList = () => {

  const [searchParams] = useSearchParams();

  const { language } = useLanguage();
  const content = translations[language as SupportedLanguage] || translations.en;
  const targetCountry = searchParams.get("targetCountry") || "";
  const category = searchParams.get("category") || "";

  const [itemsPerPage, setItemsPerPage] = useState<number>(5);
  const [page, setPage] = useState<number>(1);
  const [selectedTag, setSelectedTag] = useState<string | null>(null);
  const [selectedCategory, setSelectedCategory] = useState<number | undefined>(
    category && !isNaN(parseInt(category, 10)) ? parseInt(category, 10) : undefined
  );

  const [titleSearch, setTitleSearch] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [resultOffer, setResultOffer] = useState<any>(null);

  const debouncedTitleSearch = useDebounce(titleSearch);


  useEffect(() => {
      const handleFetchOffer = async () => {
        setLoading(true);
        setError(null);
        try {
          const response = await getOfferListByTag({
            language: language,
            itemsPerPage: itemsPerPage,
            page: page,
            tag: selectedTag,
            search: debouncedTitleSearch,
            targetCountry: targetCountry,
            categoryId: selectedCategory
          });
          setResultOffer(response);
        } catch (err: any) {
          setError(err.message || "An error occurred");
        } finally {
          setLoading(false);
        }
      };

      handleFetchOffer();
  }, [itemsPerPage, language, page, selectedTag, selectedCategory, debouncedTitleSearch, targetCountry]);

  const handleChangeCurrentPage = (newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (newRowsPerPage: number) => {
    setItemsPerPage(newRowsPerPage);
    setPage(1); // Reset to the first page when rows per page change
  };

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: "#0b0f17", color: "#ffffff", py: { xs: 4, md: 7 } }}>
      <Container maxWidth="xl">
        <Stack
          direction={{ xs: "column", sm: "row" }}
          justifyContent="space-between"
          alignItems={{ xs: "flex-start", sm: "center" }}
          gap={2}
          sx={{ mb: { xs: 4, md: 6 } }}
        >
          <Stack spacing={1}>
            <Typography variant="h3" component="h1">{resultOffer?.data?.title}</Typography>
            <Typography sx={{ maxWidth: 680, color: "#8c94a4" }}>
              {resultOffer?.data?.subtitle}
            </Typography>
          </Stack>
        </Stack>

        <Stack direction={{ xs: "column", lg: "row" }} spacing={{ xs: 4, lg: 6 }} alignItems="flex-start">
          <TagFilter
            selectedTag={selectedTag}
            onTagSelect={setSelectedTag}
            categoryId={selectedCategory}
          />

          <Box sx={{ flex: 1, width: "100%" }}>
            <Stack
              direction={{ xs: "column", sm: "row" }}
              spacing={2}
              sx={{ mb: 3 }}
            >
              <TextField
                fullWidth
                size="small"
                value={titleSearch}
                onChange={(event) => setTitleSearch(event.target.value)}
                placeholder={content.titleSearch}
                sx={{
                  bgcolor: "#161d2b",
                  "& .MuiInputBase-input": { color: "#ffffff" },
                  "& .MuiOutlinedInput-notchedOutline": { borderColor: "#1e2638" },
                  "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "#3b82f6" },
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchRounded fontSize="small" sx={{ color: "#8c94a4" }} />
                    </InputAdornment>
                  ),
                }}
              />
              <FilterCategory
                defaultValue={selectedCategory}
                onSubmit={(categoryId) => {
                  setSelectedCategory(categoryId);
                  setPage(1);
                }}
              />
            </Stack>
            {loading && <Box sx={{ display: "flex", justifyContent: "center", py: 12 }}><CircularProgress /></Box>}
            {error && <Alert severity="error">{error}</Alert>}
            {!loading && !error && resultOffer?.data?.items?.length === 0 && (
              <Paper sx={{ p: 6, textAlign: "center", bgcolor: "#161b22", border: "1px solid #1e2638", color: "#ffffff" }}>
                <Typography variant="h6" gutterBottom sx={{ color: "#ffffff" }}>{content.emptyTitle}</Typography>
                <Typography sx={{ color: "#8c94a4" }}>{content.emptyDescription}</Typography>
              </Paper>
            )}
            {!loading && !error && resultOffer?.data?.items?.length > 0 && (
              <>
                <Grid container spacing={3}>
                  {resultOffer?.data?.items?.map((offer: any) => 
                    <Grid item key={offer.id} xs={12} md={6}>
                      <OfferCard offer={offer} />
                    </Grid>
                  )}
                </Grid>
              </>
            )}
            <PaginationComponent 
              currentPage={page}
              totalItems={resultOffer?.data?.totalItems || 0}
              rowsPerPage={itemsPerPage}
              onPageChange={handleChangeCurrentPage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          </Box>
        </Stack>
      </Container>
    </Box>
  );
};

export default PublicOfferList;
